// Minimal but usable UI. Deliberately vanilla-DOM so the bundle stays small.
// Screens: launcher -> onboarding (name + email + OTP) -> chat.
import { ApiClient } from '../api/client.js';
import { connect } from '../socket/socket.js';
import type { Socket } from 'socket.io-client';
import { injectStyles } from './styles.js';
import { ICON } from './icons.js';
import { playNotifySound, primeNotifySound } from './sound.js';
import type { SiteLogin, SiteSession, StoredSession, WidgetConfig } from './types.js';

const STORAGE_KEY = 'lcw:session';
// Fired on this page when the stored session gains a conversation in a way the
// launcher would not otherwise notice (see sessionChanged in mountWidget).
const SESSION_EVENT = 'lcw:session-changed';
// Remembers that this visitor has opened the chat, so the launcher stops
// waving at someone who already knows it is there.
const SEEN_KEY = 'lcw:seen';
// Used only when the admin's list is missing — normally it comes from settings.
const QUICK_EMOJI = ['👍', '❤️', '😂', '😮', '🙏'];

// A message that could not be sent because the sign-in had ended, kept so it
// is waiting in the box once the visitor has signed in again.
let unsentDraft = '';

/**
 * Bar heights for a voice note. Decorative, not a real waveform — computing
 * actual amplitudes would mean downloading and decoding every clip in the
 * thread just to draw it. Derived from the message id so a given clip always
 * looks the same, and reused as the progress track.
 */
function voiceBars(seed: string, count = 30): number[] {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) % 100003;
  return Array.from({ length: count }, (_, i) => {
    h = (h * 1103515245 + 12345) % 2147483648;
    const n = (h / 2147483648) * 0.7 + 0.3;
    const taper = Math.sin((i / (count - 1)) * Math.PI) * 0.35 + 0.65;
    return Math.round(n * taper * 100);
  });
}

/**
 * Human label for a message with no text body — quote bars were showing the
 * raw type name (`[image]`), leaking an internal value into the UI.
 */
const MEDIA_LABEL: Record<string, string> = {
  image: '📷 Photo',
  voice: '🎤 Voice message',
  video: '🎬 Video',
  file: '📎 Attachment',
  deleted: 'This message was deleted',
};

function messageSummary(m: { type: string; body?: string | null } | null): string {
  if (!m) return '';
  if (m.type === 'deleted') return MEDIA_LABEL.deleted;
  if (m.body) return m.body;
  return MEDIA_LABEL[m.type] ?? 'Attachment';
}

/**
 * Attachment card for non-media files, mirroring the admin console.
 *
 * Keyed off the filename extension rather than the MIME type: browsers and
 * operating systems disagree about MIME for Office formats (.docx can arrive
 * as octet-stream), while the extension the user picked is stable.
 */
const DOC_KINDS: Record<string, { icon: string; hue: number; label: string }> = {
  pdf:    { icon: ICON.docText,   hue: 25,  label: 'PDF' },
  word:   { icon: ICON.docText,   hue: 255, label: 'Document' },
  sheet:  { icon: ICON.docSheet,  hue: 150, label: 'Spreadsheet' },
  slides: { icon: ICON.docSlides, hue: 55,  label: 'Presentation' },
  archive:{ icon: ICON.docZip,    hue: 85,  label: 'Archive' },
  code:   { icon: ICON.docText,   hue: 200, label: 'Text' },
  generic:{ icon: ICON.docFile,   hue: 265, label: 'File' },
};

const DOC_BY_EXT: Record<string, string> = {
  pdf: 'pdf',
  doc: 'word', docx: 'word', odt: 'word', rtf: 'word',
  xls: 'sheet', xlsx: 'sheet', ods: 'sheet', csv: 'sheet',
  ppt: 'slides', pptx: 'slides',
  zip: 'archive', rar: 'archive', '7z': 'archive', gz: 'archive',
  txt: 'code', md: 'code', json: 'code', xml: 'code',
};

function formatBytes(bytes?: number | null): string {
  if (!bytes || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function buildDocumentCard(url: string, name: string | null, sizeBytes?: number | null): HTMLElement {
  const filename = (name ?? '').trim() || 'Attachment';
  const ext = (/\.([a-z0-9]+)$/i.exec(filename)?.[1] ?? '').toLowerCase();
  const kind = DOC_KINDS[DOC_BY_EXT[ext] ?? 'generic'];

  const a = document.createElement('a');
  a.className = 'lcw-doc';
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener';
  a.download = filename;
  a.title = filename;

  const tile = document.createElement('span');
  tile.className = 'lcw-doc-tile';
  // Mid-lightness tile with a white glyph reads the same on either bubble.
  tile.style.background = `oklch(0.58 0.15 ${kind.hue})`;
  tile.innerHTML = kind.icon;

  const textWrap = document.createElement('span');
  textWrap.className = 'lcw-doc-text';
  const nameEl = document.createElement('strong');
  nameEl.textContent = filename;
  const metaEl = document.createElement('span');
  metaEl.textContent = [ext ? ext.toUpperCase() : kind.label, formatBytes(sizeBytes)]
    .filter(Boolean)
    .join(' · ');
  textWrap.append(nameEl, metaEl);

  const dl = document.createElement('span');
  dl.className = 'lcw-doc-dl';
  dl.innerHTML = ICON.download;

  a.append(tile, textWrap, dl);
  return a;
}

// A web address inside a line of text, up to the first space or quote.
const LINK_IN_TEXT = /https?:\/\/[^\s<>"'`]+/gi;

/**
 * An address as found in running text, without the punctuation that follows
 * it: the full stop that ends the sentence, a closing bracket that was opened
 * before the address began. A bracket the address opened itself is kept.
 * (The same rule as the server's, backend services/linkCards.ts.)
 */
function trimLink(found: string): string {
  let url = found;
  for (;;) {
    const before = url;
    url = url.replace(/[.,;:!?]+$/, '');
    for (const [open, close] of [['(', ')'], ['[', ']'], ['{', '}']]) {
      if (url.endsWith(close) && url.split(close).length > url.split(open).length) url = url.slice(0, -1);
    }
    if (url === before) return url;
  }
}

/** A web address that is safe to put in href or src, or null. */
function webUrl(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  try {
    const u = new URL(raw.trim());
    return u.protocol === 'http:' || u.protocol === 'https:' ? u.href : null;
  } catch {
    return null;
  }
}

/**
 * Put a message's text into `el` with its web addresses clickable.
 *
 * Built from text nodes and <a> elements, never from markup: what someone
 * typed is only ever shown as text, whatever it contains.
 */
function appendLinked(el: HTMLElement, text: string): void {
  let last = 0;
  for (const m of text.matchAll(LINK_IN_TEXT)) {
    const shown = trimLink(m[0]);
    const start = m.index ?? 0;
    const href = webUrl(shown);
    if (!href) continue;
    if (start > last) el.append(text.slice(last, start));
    const a = document.createElement('a');
    a.href = href;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.textContent = shown;
    // Otherwise the bubble's own click handler swallows it.
    a.addEventListener('click', (e) => e.stopPropagation());
    el.append(a);
    last = start + shown.length;
  }
  if (last < text.length) el.append(text.slice(last));
}

/**
 * Link card: what the website itself said about one of its links (the server
 * attaches it, see backend services/linkCards.ts). A picture, a small label, a
 * title, a line of text and a button, all of it one link.
 *
 * Every field is set as text. Nothing a website sends is treated as markup.
 */
function buildLinkCard(card: any): HTMLElement | null {
  const href = webUrl(card?.url);
  const title = typeof card?.title === 'string' ? card.title.trim() : '';
  if (!href || !title) return null;

  const a = document.createElement('a');
  a.className = 'lcw-card';
  a.href = href;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  a.addEventListener('click', (e) => e.stopPropagation());

  const src = webUrl(card.image_url);
  if (src) {
    const img = document.createElement('img');
    img.className = 'lcw-card-img';
    img.src = src;
    img.alt = '';
    img.loading = 'lazy';
    // A picture that will not load leaves a card without one, not a broken frame.
    img.addEventListener('error', () => img.remove());
    a.appendChild(img);
  }

  const body = document.createElement('span');
  body.className = 'lcw-card-body';
  const line = (cls: string, tag: string, text: unknown) => {
    if (typeof text !== 'string' || !text.trim()) return;
    const node = document.createElement(tag);
    node.className = cls;
    node.textContent = text.trim();
    body.appendChild(node);
  };
  line('lcw-card-label', 'span', card.label);
  line('lcw-card-title', 'strong', title);
  line('lcw-card-desc', 'span', card.description);
  line('lcw-card-cta', 'span', typeof card.cta === 'string' && card.cta.trim() ? card.cta : 'Open');
  // Where the button leads, always in sight: the words on a card are the
  // website's own, the address under them is a fact.
  line('lcw-card-host', 'span', new URL(href).host.replace(/^www\./, ''));
  a.appendChild(body);
  return a;
}

/**
 * Full-screen image preview. Appended to <body> rather than inside the panel
 * so it isn't clipped by the panel's own overflow, and so it can use the whole
 * screen on a phone.
 */
function openLightbox(url: string, alt: string) {
  const overlay = document.createElement('div');
  overlay.className = 'lcw-lightbox';

  const img = document.createElement('img');
  img.src = url;
  img.alt = alt;
  // Clicking the photo must not dismiss — it's the biggest target in the
  // overlay and the easiest thing to hit by accident.
  img.addEventListener('click', (e) => e.stopPropagation());

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'lcw-lightbox-close';
  close.setAttribute('aria-label', 'Close image');
  close.innerHTML = ICON.close;

  function dismiss() {
    overlay.remove();
    document.removeEventListener('keydown', onKey);
  }
  function onKey(e: KeyboardEvent) {
    if (e.key === 'Escape') dismiss();
  }

  overlay.addEventListener('click', dismiss);
  close.addEventListener('click', dismiss);
  document.addEventListener('keydown', onKey);

  overlay.append(img, close);
  document.body.appendChild(overlay);
}

function fmtTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Custom voice player. The native <audio controls> paints its own light chrome
 * with a volume slider and overflow menu, which is both oversized for the panel
 * and visually wrong on a dark bubble.
 */
function buildVoiceNote(url: string, seed: string, durationMs?: number | null): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'lcw-voice';

  const audio = document.createElement('audio');
  audio.src = url;
  audio.preload = 'metadata';
  wrap.appendChild(audio);

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'lcw-voice-play';
  btn.setAttribute('aria-label', 'Play voice message');
  btn.innerHTML = ICON.play;
  wrap.appendChild(btn);

  const barsEl = document.createElement('div');
  barsEl.className = 'lcw-voice-bars';
  const heights = voiceBars(seed);
  const barNodes = heights.map((h) => {
    const b = document.createElement('span');
    b.style.height = `${Math.max(12, h)}%`;
    barsEl.appendChild(b);
    return b;
  });
  wrap.appendChild(barsEl);

  const time = document.createElement('span');
  time.className = 'lcw-voice-time';
  // Prefer the recorded duration: MediaRecorder WebM often reports Infinity
  // until the file has been fully seeked.
  let duration = durationMs ? durationMs / 1000 : 0;
  time.textContent = fmtTime(duration);
  wrap.appendChild(time);

  function paint(progress: number) {
    barNodes.forEach((b, i) => {
      b.classList.toggle('played', i / barNodes.length <= progress);
    });
  }

  audio.addEventListener('loadedmetadata', () => {
    if (Number.isFinite(audio.duration) && audio.duration > 0) {
      duration = audio.duration;
      if (audio.paused) time.textContent = fmtTime(duration);
    }
  });
  audio.addEventListener('timeupdate', () => {
    time.textContent = fmtTime(audio.currentTime);
    paint(duration > 0 ? audio.currentTime / duration : 0);
  });
  audio.addEventListener('ended', () => {
    btn.innerHTML = ICON.play;
    btn.setAttribute('aria-label', 'Play voice message');
    time.textContent = fmtTime(duration);
    paint(0);
  });

  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    if (audio.paused) {
      void audio.play();
      btn.innerHTML = ICON.pause;
      btn.setAttribute('aria-label', 'Pause voice message');
    } else {
      audio.pause();
      btn.innerHTML = ICON.play;
      btn.setAttribute('aria-label', 'Play voice message');
    }
  });

  barsEl.addEventListener('click', (e) => {
    e.stopPropagation();
    if (!duration) return;
    const r = barsEl.getBoundingClientRect();
    audio.currentTime = ((e.clientX - r.left) / r.width) * duration;
  });

  return wrap;
}

function loadSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredSession) : null;
  } catch {
    return null;
  }
}
function saveSession(s: StoredSession) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
}

/**
 * Take a session handed to us by the page rather than earned through OTP.
 *
 * Used by the hosted chat page, which is reached from a link in an email that
 * already proved the visitor controls that address. Stored like any other
 * session so closing the tab and coming back still works.
 */
export function adoptSession(s: StoredSession) {
  saveSession(s);
}
function clearSession() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // private mode / storage disabled — nothing to clear
  }
}

/*
 * Website sign-in (window.LCW_SITE, see types.ts).
 *
 * A sign-in the website fetched is kept like any other, marked with where it
 * came from and for whom. That mark is what lets the next page tell the three
 * cases apart without asking anyone: the same person again (use what is
 * stored), someone else logged in on this browser (ask), nobody logged in any
 * more (drop it, so the next person at this computer does not open the last
 * one's conversation).
 */

/**
 * Does this sign-in say which website vouched for it? Ones given out before
 * "On the website now" existed do not, and the server lists nobody without it
 * (the claim is what it trusts, not this check: see signVisitorToken there).
 * Such a sign-in is swapped for a new one the next time a page loads -- asked
 * for once per browser session, in case the answer is the same again.
 */
function tokenNamesSite(token: string): boolean {
  try {
    const claims = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof claims?.site === 'string' && claims.site !== '';
  } catch {
    return false;
  }
}
function askedForNamedToken(): boolean {
  try {
    if (sessionStorage.getItem('lcw:site-named') === '1') return true;
    sessionStorage.setItem('lcw:site-named', '1');
    return false;
  } catch {
    // No session storage: do not ask on every page for ever.
    return true;
  }
}

/** Renewed after this long; the sign-in itself lasts seven days. */
const SITE_SESSION_FRESH_MS = 5 * 24 * 60 * 60 * 1000;
/** How long a page will be kept waiting for the website's answer. */
const SITE_ASK_TIMEOUT_MS = 6000;
/**
 * After the website declined or could not answer, how long before it is asked
 * again for the same user. Without this, a chat server that is down -- or a
 * key that was replaced and not yet pasted into the site -- would be asked
 * about on every page every logged-in person opens, each one holding up the
 * launcher and a worker on the website's server.
 */
const SITE_ASK_AGAIN_MS = 3 * 60 * 1000;
const SITE_WAIT_KEY = 'lcw:site-wait';

function siteAskBlocked(user: string): boolean {
  try {
    const w = JSON.parse(sessionStorage.getItem(SITE_WAIT_KEY) || 'null') as { user?: string; until?: number } | null;
    return !!w && w.user === user && typeof w.until === 'number' && w.until > Date.now();
  } catch {
    return false;
  }
}
function siteAskLater(user: string): void {
  try {
    sessionStorage.setItem(SITE_WAIT_KEY, JSON.stringify({ user, until: Date.now() + SITE_ASK_AGAIN_MS }));
  } catch {
    // storage unavailable: it is asked on the next page, which is only slower
  }
}
function siteAskCleared(): void {
  try {
    sessionStorage.removeItem(SITE_WAIT_KEY);
  } catch {
    // nothing to clear
  }
}

async function askSite(site: SiteLogin, fresh: boolean): Promise<SiteSession | null | undefined> {
  if (typeof site.session !== 'function') return undefined;
  try {
    const answer = await Promise.race([
      Promise.resolve(site.session(fresh)),
      new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), SITE_ASK_TIMEOUT_MS)),
    ]);
    if (answer === null) return null;
    if (
      answer &&
      typeof answer.visitorToken === 'string' &&
      answer.visitorToken &&
      typeof answer.visitorId === 'string' &&
      answer.visitorId
    ) {
      return answer;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

function keepSiteSession(s: SiteSession, user: string, before: StoredSession | null): StoredSession {
  const session: StoredSession = {
    visitorToken: s.visitorToken,
    visitorId: s.visitorId,
    email: String(s.email ?? ''),
    name: String(s.name ?? ''),
    source: 'site',
    siteUser: user,
    issuedAt: Date.now(),
    // The same person as before keeps the thread already found for them; a
    // different person must never inherit it.
    ...(before && before.visitorId === s.visitorId && before.conversationId
      ? { conversationId: before.conversationId }
      : {}),
  };
  try {
    saveSession(session);
  } catch {
    // Storage is full or switched off. What matters then is that whatever was
    // stored before -- possibly someone else's sign-in -- is not left there.
    clearSession();
  }
  siteAskCleared();
  return session;
}

/**
 * Bring the stored sign-in in line with who is logged in to the website.
 * Called once, before the widget is drawn. Never throws.
 */
export async function syncSiteLogin(site: SiteLogin): Promise<void> {
  const stored = loadSession();
  const user = String(site.user ?? '');
  if (!user || typeof site.session !== 'function') {
    // Nobody is logged in to the website. A sign-in the website gave out goes
    // with them; one earned with a code is the visitor's own and stays.
    if (stored?.source === 'site') clearSession();
    return;
  }
  let mine = stored;
  if (stored?.source === 'site' && stored.siteUser !== user) {
    // A sign-in the website gave to someone else who used this browser. It
    // goes now, before anything is asked: if the website cannot answer for the
    // new person just yet, they get the ordinary form -- never the last
    // person's conversation.
    clearSession();
    mine = null;
  }
  if (
    mine?.source === 'site' &&
    mine.visitorToken &&
    Date.now() - (mine.issuedAt ?? 0) < SITE_SESSION_FRESH_MS &&
    (tokenNamesSite(mine.visitorToken) || askedForNamedToken())
  ) {
    return;
  }
  if (siteAskBlocked(user)) return;
  const answer = await askSite(site, false);
  if (answer) {
    keepSiteSession(answer, user, mine);
    return;
  }
  siteAskLater(user);
  if (answer === null && mine?.source === 'site') {
    // The website no longer vouches for this person.
    clearSession();
  }
  // undefined: could not ask. Their own earlier sign-in, if any, is left as it
  // is; it runs out by itself if it is never renewed.
}

/**
 * The page says nothing about website sign-in at all: the site has switched it
 * off, or this is a page of some other kind. A sign-in the website gave out
 * earlier has nobody vouching for it any more, and logging out of the website
 * could no longer end it, so it is dropped here. One earned with a code stays.
 */
export function dropSiteSession(): void {
  if (loadSession()?.source === 'site') clearSession();
}

/**
 * The chat server refused a sign-in the website had given us (its keys were
 * changed, or it simply ran out). Ask the website for a new one, once.
 */
async function renewSiteSession(old: StoredSession): Promise<StoredSession | null> {
  const site = window.LCW_SITE;
  if (!site || old.source !== 'site' || !old.siteUser || String(site.user ?? '') !== old.siteUser) return null;
  const answer = await askSite(site, true);
  if (!answer || answer.visitorToken === old.visitorToken) return null;
  return keepSiteSession(answer, old.siteUser, old);
}

/**
 * Tell the chat server which page this person has open, for the console's
 * "On the website now". Only ever sent for someone the website itself signed
 * in: they are that site's own customer, on that site. The path only, never
 * what follows a `?` or `#`: links from emails carry access keys there.
 */
function sayHere(socket: Socket): void {
  socket.emit('site:here', { path: location.pathname, title: document.title.slice(0, 150) });
}

/** The same site, with or without www -- the server's rule (services/domains). */
function sameSite(a: string, b: string): boolean {
  const bare = (h: string) => h.toLowerCase().replace(/^www\./, '');
  return bare(a) === bare(b);
}

/**
 * Shown when the thread can't be loaded but the session still looks valid —
 * a dropped connection, say. Keeps the session so a retry resumes rather than
 * dragging the visitor back through OTP.
 */
function renderChatError(root: HTMLElement, config: WidgetConfig, session: StoredSession) {
  root.innerHTML = `
    ${headerHtml(config)}
    <div class="lcw-form">
      <p class="lcw-hint">We couldn't load your conversation just now.</p>
      <button class="lcw-btn lcw-retry" type="button">Try again</button>
    </div>
  `;
  root.querySelector('.lcw-close')?.addEventListener('click', () => root.remove());
  (root.querySelector('.lcw-retry') as HTMLButtonElement).addEventListener('click', () => {
    void renderChat(root, config, session);
  });
}

/**
 * `signedIn` adds the sign-out control. It is offered only where there is a
 * session to end -- on the onboarding screen the button would do nothing and
 * only raise the question of what it does.
 */
function isTouch(): boolean {
  return window.matchMedia('(hover: none)').matches;
}

function headerHtml(config: WidgetConfig, signedIn = false): string {
  const mark = config.logoUrl
    ? `<img class="lcw-logo" src="${escapeAttr(config.logoUrl)}" alt="" />`
    : ICON.chat;
  // Close puts the panel away and leaves the launcher to bring it back. The
  // hosted chat page has no launcher: closing there empties the window and
  // there is nothing left to press, so the control is dropped rather than
  // offered as a one-way door. Every listener for it is optional-chained.
  const close = window.LCW_STANDALONE
    ? ''
    : `<button class="lcw-close" aria-label="Close">${ICON.close}</button>`;
  return `
    <div class="lcw-header">
      <span class="lcw-header-chip" aria-hidden="true">${mark}</span>
      <span class="lcw-header-title">${escapeHtml(config.brandName)}</span>
      ${signedIn ? '<button class="lcw-signout" type="button">Sign out</button>' : ''}
      ${close}
    </div>
  `;
}

/**
 * These strings come from the admin's settings, not from a visitor, so this is
 * defence in depth rather than a live hole -- but they are interpolated into
 * innerHTML on someone else's website, and an escape here costs nothing.
 */
function escapeHtml(v: string): string {
  return v.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}
function escapeAttr(v: string): string {
  return escapeHtml(v);
}

export interface MountOptions {
  /** Open the panel straight away instead of waiting for a click. */
  autoOpen?: boolean;
  /** The widget is the whole page, not a launcher on someone's site. */
  standalone?: boolean;
}

export function mountWidget(config: WidgetConfig, opts: MountOptions = {}) {
  injectStyles(config);

  // Unlock the notification chime on the visitor's first interaction with the
  // page, whatever it is -- not necessarily one aimed at the widget. Without
  // this, the audio context stays suspended (silently -- nothing throws)
  // until some click finally reaches it, and a reply arriving before then
  // plays into nothing. { once: true, capture: true } catches the click even
  // if the page's own script stops it from bubbling.
  const unlockAudio = () => {
    primeNotifySound();
    document.removeEventListener('pointerdown', unlockAudio, true);
    document.removeEventListener('keydown', unlockAudio, true);
  };
  document.addEventListener('pointerdown', unlockAudio, { once: true, capture: true });
  document.addEventListener('keydown', unlockAudio, { once: true, capture: true });

  const launcher = document.createElement('button');
  launcher.className = 'lcw-launcher';
  launcher.setAttribute('aria-label', config.hoverMessage || `Chat with ${config.brandName}`);

  /*
   * The mascot, and what its face is for.
   *
   * An uploaded logo is a fixed image and stays put — someone who supplied
   * their own mark does not want it swapped for a crocodile. Without one, the
   * built-in croc reacts, because a face that changes with what is happening
   * says more than a badge that never moves: it is waiting, it heard you, an
   * agent is typing.
   *
   * Served as files rather than inlined: nine sprites is most of a megabyte
   * once base64'd, added to a bundle every visitor downloads, to show one.
   */
  // An explicit setting, not "did they upload something". A logo can be
  // uploaded for the header while the launcher keeps the animated croc.
  const useMascot = config.useMascot !== false || !config.logoUrl;
  const faceUrl = (name: string) => `${config.apiUrl}/widget/croc/${name}.png`;
  const mark = document.createElement('img');
  // The mascot and an uploaded logo want opposite treatment: a customer's mark
  // is cropped to fill the circle, the croc keeps its own bubble outline.
  mark.className = useMascot ? 'lcw-logo lcw-mascot' : 'lcw-logo';
  mark.alt = '';
  mark.src = useMascot ? faceUrl('idle') : config.logoUrl;
  /*
   * If the mark cannot load, don't leave an empty button.
   *
   * The launcher drops its accent circle when it carries an image, because the
   * sprites are bubble-shaped and a coloured disc behind them shows at the
   * edges. The cost is that a failed image leaves an invisible button with a
   * broken-image glyph in the corner — which is exactly what a stale cache
   * after a deploy produces. Swapping in the built-in glyph also stops
   * :has(img.lcw-logo) matching, so the accent circle comes back by itself.
   */
  mark.addEventListener('error', () => {
    const fallback = document.createElement('span');
    fallback.className = 'lcw-mark-fallback';
    fallback.innerHTML = ICON.chat;
    mark.replaceWith(fallback);
  });
  launcher.appendChild(mark);

  /*
   * The hover sequence.
   *
   * Five frames of one movement, not five expressions: the eyes lift, blink,
   * open wide and settle again. It is the eyes and not the mouth because there
   * is a single frame of source art to cut these from, and the eyes are the
   * part of it that can be moved without redrawing it — scripts/cut-croc-sprites.py
   * has the whole argument.
   *
   * The blink lands second rather than first. It is the one frame here that
   * reads on its own at launcher size, and spending it on the same tick the
   * pointer arrives wastes it; a beat later, it looks like a reaction.
   */
  const GREET = ['smile', 'blink', 'wide', 'happy', 'calm'];

  let faceTimer: ReturnType<typeof setTimeout> | null = null;
  let seqTimer: ReturnType<typeof setInterval> | null = null;

  function stopAnimation() {
    if (faceTimer) { clearTimeout(faceTimer); faceTimer = null; }
    if (seqTimer) { clearInterval(seqTimer); seqTimer = null; }
  }

  /** `holdMs` returns to the resting face afterwards; otherwise it stays. */
  function setFace(name: string, holdMs?: number) {
    if (!useMascot) return;
    stopAnimation();
    mark.src = faceUrl(name);
    if (holdMs) faceTimer = setTimeout(() => { mark.src = faceUrl(restingFace()); }, holdMs);
  }

  /** Play frames in order, then settle back to whatever the state calls for. */
  function playFrames(frames: string[], frameMs = 130) {
    if (!useMascot) return;
    stopAnimation();
    let i = 0;
    mark.src = faceUrl(frames[0]);
    seqTimer = setInterval(() => {
      i += 1;
      if (i >= frames.length) {
        stopAnimation();
        mark.src = faceUrl(restingFace());
        return;
      }
      mark.src = faceUrl(frames[i]);
    }, frameMs);
  }
  // Waiting to be read outranks everything else it could be showing.
  const restingFace = () => (unread > 0 ? 'excited' : 'idle');

  // Warm the ones that appear in response to something, so the swap is instant
  // rather than a blank circle while a request goes out.
  if (useMascot) {
    for (const n of [...GREET, 'excited', 'typing', 'wink']) {
      const pre = new Image();
      pre.src = faceUrl(n);
    }
  }

  document.body.appendChild(launcher);

  /*
   * The greeting beside the launcher.
   *
   * Shown from the moment the widget loads rather than on hover. Hover was
   * never the right trigger: a phone has no hover at all, so half the audience
   * never saw the message, and on a desktop it asked someone to find the
   * launcher before being told why they should press it.
   *
   * Being permanent, it has to be dismissible. The dismissal is remembered
   * against the message text, so a visitor who closes it is not nagged on every
   * page — but changing the greeting in Settings shows it to them again, which
   * is what an operator writing a new one expects.
   *
   * A real element rather than the `title` attribute: title is unstyleable,
   * takes a second to appear, and never shows on touch. The launcher still
   * carries an aria-label, since a screen reader should not depend on this.
   */
  const TIP_KEY = 'lcw-tip-dismissed';
  const tipAlreadyDismissed = () => {
    try {
      return localStorage.getItem(TIP_KEY) === config.hoverMessage;
    } catch {
      return false;
    }
  };

  let tip: HTMLDivElement | null = null;
  const dismissTip = () => {
    try {
      localStorage.setItem(TIP_KEY, config.hoverMessage || '');
    } catch {
      // A browser refusing storage means it reappears next visit. Acceptable —
      // far better than the close button doing nothing.
    }
    tip?.remove();
    tip = null;
  };

  // On the hosted chat page there is no launcher for the tip to invite someone
  // toward -- it opens straight to the conversation -- so the greeting bubble
  // has nothing to point at and would just float on its own.
  if (config.hoverMessage && !opts.standalone && !tipAlreadyDismissed()) {
    tip = document.createElement('div');
    tip.className = 'lcw-tip';

    const tipText = document.createElement('span');
    tipText.className = 'lcw-tip-text';
    tipText.textContent = config.hoverMessage;
    // Pressing the message itself opens the chat, which is what it is inviting
    // people to do.
    tipText.addEventListener('click', () => launcher.click());

    const tipClose = document.createElement('button');
    tipClose.className = 'lcw-tip-close';
    tipClose.type = 'button';
    tipClose.setAttribute('aria-label', 'Dismiss this message');
    tipClose.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
    tipClose.addEventListener('click', (event) => {
      event.stopPropagation();
      dismissTip();
    });

    tip.append(tipText, tipClose);
    // Shown immediately rather than on the next animation frame. rAF does not
    // run in a page that is not being painted, so a site opened in a background
    // tab -- an extremely ordinary thing -- left the greeting invisible until
    // the visitor happened to focus it. The entrance is a CSS animation now,
    // which plays when the element is inserted and needs no frame from us.
    tip.classList.add('on');
    document.body.appendChild(tip);
  }

  /** Hidden only while the chat panel is open — it would sit on top of it. */
  const showTip = (on: boolean) => {
    if (!tip) return;
    tip.classList.toggle('on', on && !panel);
  };
  /*
   * Hover animates the croc; it no longer governs the message. The greeting is
   * already on screen, and hiding it when the pointer left would have made it
   * flicker every time somebody crossed the corner of the page.
   */
  launcher.addEventListener('mouseenter', () => playFrames(GREET));
  launcher.addEventListener('mouseleave', () => setFace(restingFace()));
  launcher.addEventListener('focus', () => playFrames(GREET));
  launcher.addEventListener('blur', () => setFace(restingFace()));

  let panel: HTMLDivElement | null = null;

  // Someone who has opened the chat before does not need waving at.
  try {
    if (localStorage.getItem(SEEN_KEY)) launcher.classList.add('lcw-quiet');
  } catch {
    // storage disabled — it just waves again, which is no worse than before
  }

  /*
   * Watching for replies while the panel is closed.
   *
   * Without this the widget is deaf until someone opens it: an agent could
   * answer within seconds and the visitor would only find out from an email
   * five minutes later, or never. A closed launcher that knows something is
   * waiting is the difference between a conversation and a missed one.
   *
   * Only runs once there is a session and a conversation — there is nothing to
   * listen to before that, and holding a socket open for every visitor who
   * never starts a chat would be rude to the server.
   *
   * One exception: someone the website itself signed in. Theirs is held open
   * on every page even with no conversation, because it is also how the
   * console's "On the website now" knows they are here (see sayHere), and how
   * they hear at once when an agent writes to them first.
   */
  let watcher: Socket | null = null;
  let unread = 0;
  const badge = document.createElement('span');
  badge.className = 'lcw-badge';
  badge.hidden = true;
  badge.setAttribute('aria-hidden', 'true');
  launcher.appendChild(badge);

  function paintUnread() {
    if (unread > 0) {
      badge.textContent = unread > 9 ? '9+' : String(unread);
      badge.hidden = false;
      launcher.classList.add('lcw-alert');
      launcher.setAttribute(
        'aria-label',
        `${unread} unread ${unread === 1 ? 'message' : 'messages'} from ${config.brandName}`,
      );
      setFace('excited');
    } else {
      badge.hidden = true;
      launcher.classList.remove('lcw-alert');
      launcher.setAttribute('aria-label', config.hoverMessage || `Chat with ${config.brandName}`);
      setFace('idle');
    }
  }

  function stopWatching() {
    watcher?.disconnect();
    watcher = null;
    watching = undefined;
  }

  // A person the website signed in may have a conversation they have not
  // opened on this browser yet -- an agent wrote to them first, or they chatted
  // on another device. Without its id the launcher cannot listen for replies,
  // and a reply would wait unseen until they happened to open the chat. Asked
  // once per page, and only when it is not known already. Never creates one.
  let lookedForThread = false;
  function findSiteThread(session: StoredSession) {
    if (lookedForThread) return;
    lookedForThread = true;
    void new ApiClient(config, session.visitorToken).listMine().then((list) => {
      // The one the server would resume (POST /api/conversations): the thread
      // on this site that was written in most recently.
      const stamp = (c: { last_message_at?: string | null; created_at?: string | null }) =>
        Date.parse(c.last_message_at || '') || Date.parse(c.created_at || '') || 0;
      const mine = list
        .filter((c) => typeof c.domain === 'string' && sameSite(c.domain, location.hostname))
        .sort((a, b) => stamp(b) - stamp(a))[0];
      const now = loadSession();
      if (!now || now.visitorToken !== session.visitorToken) return;
      // Another tab may have found (or been told of) the thread meanwhile.
      // Then there is nothing to save, but this tab still has to listen in it.
      if (!now.conversationId) {
        if (!mine) return;
        saveSession({ ...now, conversationId: mine.id });
      }
      // This answer also says how much of that thread is waiting unread, so
      // seedUnread need not ask for the same list a moment later.
      const thread = list.find((c) => c.id === loadSession()?.conversationId);
      if (thread) {
        askedUnread = true;
        const waiting = Number(thread.unread ?? 0);
        if (!panel && waiting > unread) {
          unread = waiting;
          paintUnread();
        }
      }
      sessionChanged();
    });
  }

  /*
   * What an agent wrote while this person was not looking: on another page, in
   * the moment between two pages, or before they came to the site today. The
   * count in memory starts at nothing on every page, so without this a reply
   * was announced only on the page that happened to be open when it arrived,
   * and forgotten on the next. Asked once per page; never lowers a count that
   * live messages have already raised.
   */
  let askedUnread = false;
  function seedUnread(session: StoredSession, cid: string) {
    if (askedUnread) return;
    askedUnread = true;
    void new ApiClient(config, session.visitorToken).listMine().then((list) => {
      const waiting = Number(list.find((c) => c.id === cid)?.unread ?? 0);
      if (panel || !(waiting > unread) || loadSession()?.conversationId !== cid) return;
      unread = waiting;
      paintUnread();
    });
  }

  /** The conversation the launcher's connection is listening in, if any. */
  let watching: string | undefined;
  // A website sign-in that the chat server no longer accepts is asked for
  // again once per page, not in a loop.
  let renewedForWatcher = false;

  function startWatching() {
    if (watcher) return;
    const session = loadSession();
    if (session?.visitorToken && !session.conversationId && session.source === 'site') findSiteThread(session);
    if (!session?.visitorToken) return;
    const cid = session.conversationId;
    const signedInBySite = session.source === 'site';
    if (!cid && !signedInBySite) return;
    const token = session.visitorToken;
    watching = cid;
    if (cid) seedUnread(session, cid);
    watcher = connect(config, token);
    // On every connect, not once. A join belongs to the connection it was made
    // on, so after a dropped connection -- the visitor's wifi, or the server
    // restarting for a deploy -- the socket came back connected to nothing and
    // replies stopped arriving until the page was reloaded.
    const joinWatcher = watcher;
    joinWatcher.on('connect', () => {
      if (signedInBySite) sayHere(joinWatcher);
      if (cid) joinWatcher.emit('conversation:join', cid);
    });
    watcher.on('message', (msg: any) => {
      // An open panel has its own socket and renders these itself; the
      // visitor's own messages come back through here too.
      if (panel || !cid || msg.conversation_id !== cid || msg.sender_type !== 'admin') return;
      unread += 1;
      paintUnread();
      playNotifySound();
    });
    // Someone is composing a reply. Worth showing from the closed launcher —
    // it is the difference between "nobody is there" and "hold on".
    watcher.on('typing', (p: { actor: string; isTyping: boolean }) => {
      if (panel || p.actor !== 'admin') return;
      if (p.isTyping) setFace('typing');
      else setFace(restingFace());
    });
    // An agent has just written to this person first, in a conversation this
    // browser has not heard of: it was made a moment ago, from the console.
    // Its first message has already been sent to a room nobody here was in,
    // so it is counted now, and the conversation is listened to from here on.
    // Every open tab is told; each counts it for itself, and only the first
    // has anything to save.
    watcher.on('conversation:started', (p: { conversationId?: unknown; domain?: unknown }) => {
      if (panel || cid || typeof p?.conversationId !== 'string') return;
      if (typeof p.domain !== 'string' || !sameSite(p.domain, location.hostname)) return;
      const now = loadSession();
      if (!now || now.visitorToken !== token) return;
      if (now.conversationId && now.conversationId !== p.conversationId) return;
      if (!now.conversationId) saveSession({ ...now, conversationId: p.conversationId });
      unread += 1;
      paintUnread();
      playNotifySound();
      // The count is already right; no need to ask the server for it.
      askedUnread = true;
      sessionChanged();
    });
    // The chat server stopped accepting this sign-in (its keys were changed,
    // or it ran out). The website that gave it can give another, without the
    // person noticing: otherwise they would drop off "On the website now" and
    // hear nothing until they happened to open the chat.
    const renew = () => {
      if (!signedInBySite || renewedForWatcher || watcher !== joinWatcher) return;
      renewedForWatcher = true;
      void renewSiteSession(session).then((again) => {
        if (!again || panel || watcher !== joinWatcher) return;
        stopWatching();
        startWatching();
      });
    };
    joinWatcher.on('connect_error', (err: Error) => {
      if (err?.message === 'unauthorized') renew();
    });
    joinWatcher.on('disconnect', (reason: string) => {
      if (reason === 'io server disconnect') renew();
    });
  }

  /*
   * The stored session gained a conversation without this launcher hearing of
   * it: another tab saved one, or a panel that was closed while still loading
   * finished opening one. A launcher listening in no conversation starts over,
   * so that it listens in that one.
   */
  function sessionChanged() {
    if (panel || !watcher || watching) return;
    if (!loadSession()?.conversationId) return;
    stopWatching();
    startWatching();
  }
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) sessionChanged();
  });
  window.addEventListener(SESSION_EVENT, sessionChanged);
  startWatching();

  function openPanel() {
    if (panel) return;
    launcher.classList.add('lcw-quiet');
    unread = 0;
    paintUnread();
    // renderChat opens its own connection to the same room; two would double
    // every event.
    stopWatching();
    try {
      localStorage.setItem(SEEN_KEY, '1');
    } catch {
      // private mode; the class above still quiets it for this page
    }
    panel = document.createElement('div');
    panel.className = opts.standalone ? 'lcw-panel lcw-standalone' : 'lcw-panel';
    document.body.appendChild(panel);
    panelGone.observe(document.body, { childList: true });
    const session = loadSession();
    if (session?.visitorToken) {
      renderChat(panel, config, session);
    } else {
      renderOnboarding(panel, config);
    }
  }

  function panelClosed() {
    if (!panel) return;
    panel = null;
    panelGone.disconnect();
    // A nod on the way out, then back to resting.
    setFace('wink', 1600);
    // Back to listening from the corner.
    startWatching();
  }
  // The X in the panel's own header takes the panel out of the page without
  // coming through the launcher. Left unnoticed, this still believed the chat
  // was open: nothing listened for replies until the next page, the person
  // read as gone to the console, and the next press of the launcher only
  // "closed" a panel that was not there.
  const panelGone = new MutationObserver(() => {
    if (panel && !panel.isConnected) panelClosed();
  });

  launcher.addEventListener('click', () => {
    showTip(false);
    if (panel) {
      panel.remove();
      panelClosed();
      return;
    }
    openPanel();
  });

  if (opts.autoOpen) {
    // On the hosted page the launcher is redundant — the chat *is* the page.
    if (opts.standalone) launcher.style.display = 'none';
    openPanel();
  }
}

function renderOnboarding(root: HTMLElement, config: WidgetConfig) {
  root.innerHTML = `
    ${headerHtml(config)}
    <div class="lcw-form">
      <p class="lcw-hint">${escapeHtml(config.welcome)} Enter your details &mdash; we'll send a 6-digit code to your email.</p>
      <label>Full name<input class="lcw-name" type="text" autocomplete="name" placeholder="Your name" /></label>
      <label>Email<input class="lcw-email" type="email" autocomplete="email" placeholder="you@example.com" /></label>
      <button class="lcw-btn lcw-request">Send code</button>
      <div class="lcw-otp-area">
        <label>Enter 6-digit code<input class="lcw-code" type="text" inputmode="numeric" maxlength="6" placeholder="000000" /></label>
        <button class="lcw-btn lcw-verify">Verify &amp; start chat</button>
        <p class="lcw-hint">Didn't get it? Check spam, or press Send code for another one.</p>
      </div>
      <p class="lcw-error"></p>
    </div>
  `;

  const api = new ApiClient(config);
  const nameEl  = root.querySelector('.lcw-name')  as HTMLInputElement;
  const emailEl = root.querySelector('.lcw-email') as HTMLInputElement;
  const codeEl  = root.querySelector('.lcw-code')  as HTMLInputElement;
  const otpArea = root.querySelector('.lcw-otp-area') as HTMLDivElement;
  const otpHint = otpArea.querySelector('.lcw-hint') as HTMLParagraphElement;
  const errEl   = root.querySelector('.lcw-error') as HTMLParagraphElement;
  root.querySelector('.lcw-close')?.addEventListener('click', () => root.remove());

  function showError(msg: string) { errEl.textContent = msg; errEl.classList.add('on'); }
  function clearError()           { errEl.textContent = '';  errEl.classList.remove('on'); }

  const requestBtn = root.querySelector('.lcw-request') as HTMLButtonElement;
  requestBtn.addEventListener('click', async () => {
    clearError();
    const email = emailEl.value.trim();
    if (!nameEl.value.trim() || !/^\S+@\S+\.\S+$/.test(email)) {
      showError('Enter a name and a valid email.');
      return;
    }
    // Sending takes a second or two against a real SMTP server, and without
    // this the button looks inert while it happens.
    const label = requestBtn.textContent;
    requestBtn.disabled = true;
    requestBtn.textContent = 'Sending...';
    try {
      const r = await api.requestOtp(email);
      if (!r.ok) {
        if (r.error === 'cooldown') {
          showError(`A code was just sent. Check your inbox, or retry in ${r.retryAfterSeconds ?? 60}s.`);
        } else if (r.error === 'rate_limited_email') {
          showError('Too many attempts for this email, please wait.');
        } else if (r.error === 'email_not_accepted') {
          showError('Please use your regular email address. Temporary email addresses cannot be used here.');
        } else if (r.error === 'send_failed') {
          // Our side failed, not theirs -- don't imply they did anything wrong
          // or that waiting out a limit will help.
          showError('We could not send the code just now. Please try again in a moment.');
        } else {
          showError('Could not send code.');
        }
        return;
      }
      // Nothing else on screen moves when a code is re-sent: the code box is
      // already open and the button drops straight back to its old label, so
      // without a word here a second press reads as a dead button. Worth
      // saying that the first code survives, too -- the habit of assuming it
      // doesn't is what sends people back to the inbox hunting for the newest
      // mail.
      if (otpArea.classList.contains('on')) {
        otpHint.textContent = 'A new code is on its way. The earlier one still works too.';
      }
      otpArea.classList.add('on');
      codeEl.focus();
    } finally {
      requestBtn.disabled = false;
      requestBtn.textContent = label;
    }
  });

  (root.querySelector('.lcw-verify') as HTMLButtonElement).addEventListener('click', async () => {
    clearError();
    const email = emailEl.value.trim();
    const name  = nameEl.value.trim();
    const code  = codeEl.value.trim();
    if (!/^\d{6}$/.test(code)) {
      showError('Enter the 6-digit code.');
      return;
    }
    const r = await api.verifyOtp(name, email, code);
    if ('error' in r) {
      const map: Record<string, string> = {
        invalid: 'That code is not correct.',
        expired: 'That code has expired -- request a new one.',
        too_many_attempts: 'Too many attempts -- request a new code.',
        not_found: 'No pending code -- request one first.',
      };
      showError(map[r.error] ?? 'Verification failed.');
      return;
    }
    const session: StoredSession = { visitorToken: r.token, visitorId: r.visitorId, email, name };
    saveSession(session);
    renderChat(root, config, session);
  });
}

async function renderChat(root: HTMLElement, config: WidgetConfig, session: StoredSession, renewed = false) {
  // The hosted chat page opened from an emailed link has nothing to sign out
  // of *to* -- there is no launcher or other site to fall back to, just this
  // one conversation -- so the control is dropped there rather than offered
  // and doing nothing useful. The same goes for someone the website signed
  // in: the next page would sign them straight back in, so the way out is to
  // log out of the website.
  root.innerHTML = `
    ${headerHtml(config, !window.LCW_STANDALONE && session.source !== 'site')}
    <div class="lcw-body"></div>
    <div class="lcw-typing" aria-live="polite"><span></span><span></span><span></span></div>
    <div class="lcw-reply-bar">
      <div class="lcw-reply-preview"><strong></strong><span></span></div>
      <button class="lcw-reply-cancel" type="button" aria-label="Cancel reply">&times;</button>
    </div>
    <div class="lcw-blocked" role="status" hidden>You can't send messages in this chat right now.</div>
    <div class="lcw-notice" role="status" hidden></div>
    <div class="lcw-composer">
      <button class="lcw-btn ghost icon lcw-attach" title="Attach" aria-label="Attach">${ICON.attach}</button>
      <button class="lcw-btn ghost icon lcw-mic mic" title="Record voice" aria-label="Record voice">${ICON.mic}</button>
      <input type="text" class="lcw-input" placeholder="Type a message..." />
      <button class="lcw-btn icon lcw-send" aria-label="Send" title="Send">${ICON.send}</button>
      <input type="file" class="lcw-file" style="display:none" />
    </div>
  `;

  const api = new ApiClient(config, session.visitorToken);

  /** The admin's reaction set, falling back if settings arrived empty. */
  const emojiSet = () => (config.emoji.list.length ? config.emoji.list : QUICK_EMOJI);

  // Sign out drops the stored session and returns to onboarding, so a shared
  // or public machine can be handed over without the next person reading the
  // conversation. The thread itself is untouched: signing back in with the
  // same email resumes it.
  root.querySelector('.lcw-signout')?.addEventListener('click', () => {
    clearSession();
    renderOnboarding(root, config);
  });
  // Mirrors the server's per-person actor key, so reaction chips can tell
  // "mine" from "someone else's" without an extra per-viewer field.
  const myKey = `visitor:${session.visitorId}`;
  const bodyEl  = root.querySelector('.lcw-body')   as HTMLDivElement;
  const inputEl = root.querySelector('.lcw-input')  as HTMLInputElement;
  if (unsentDraft) {
    inputEl.value = unsentDraft;
    unsentDraft = '';
  }
  const fileEl  = root.querySelector('.lcw-file')   as HTMLInputElement;
  const micBtn  = root.querySelector('.lcw-mic')    as HTMLButtonElement;
  // The server refuses a blocked person's messages (spam). Say so plainly and
  // put the composer away; nothing says how they were caught.
  const showBlocked = () => {
    const note = root.querySelector('.lcw-blocked') as HTMLDivElement | null;
    if (note) note.hidden = false;
    const composer = root.querySelector('.lcw-composer') as HTMLDivElement | null;
    if (composer) composer.style.display = 'none';
  };
  const isBlockedError = (err: any) => err?.status === 403 && err?.body?.error === 'blocked';
  // A short message above the composer: a file too large, the day's limit.
  let noticeTimer = 0;
  const showNotice = (text: string) => {
    const el = root.querySelector('.lcw-notice') as HTMLDivElement | null;
    if (!el) return;
    el.textContent = text;
    el.hidden = false;
    window.clearTimeout(noticeTimer);
    noticeTimer = window.setTimeout(() => {
      el.hidden = true;
    }, 8000);
  };
  const maxFileMb = config.maxFileMb ?? 50;
  const dailyFileMb = config.dailyFileMb ?? 500;
  const tooLargeText = `The maximum file attachment size is ${maxFileMb} MB.`;
  const dailyText = `You are not allowed to send more than ${dailyFileMb} MB of files in a day.`;
  /** Explain a refused upload; true when it was one of ours. */
  const explainUploadError = (err: any): boolean => {
    if (isBlockedError(err)) {
      showBlocked();
      return true;
    }
    if (err?.status === 413) {
      const limit = Number(err?.body?.limitMb);
      showNotice(limit > 0 ? `The maximum file attachment size is ${limit} MB.` : tooLargeText);
      return true;
    }
    if (err?.status === 429 && err?.body?.error === 'daily_upload_limit') {
      showNotice(dailyText);
      return true;
    }
    if (err?.status === 429) {
      showNotice('Too many files at once. Please wait a little and try again.');
      return true;
    }
    return false;
  };

  root.querySelector('.lcw-close')?.addEventListener('click', () => root.remove());

  // Open (or resume) the visitor's conversation, then load its history.
  //
  // Resuming has to survive three states that all used to end in a blank
  // thread: an expired token (tokens last 7 days), a conversation id that no
  // longer resolves, and the backend simply being unreachable.
  //
  // Which thread belongs to this site is the server's call, never a stored
  // guess. `listMessages` hands back any conversation this visitor owns without
  // checking which site it came from, so a saved id is enough to render another
  // site's thread here -- and a browser that saved a wrong one back when the
  // server was handing them out would keep showing it forever, long after the
  // server stopped. Asking every time is one request, and it is what carries
  // the per-domain scoping in POST /api/conversations through to the visitor.
  //
  // An id the *page* handed us is the exception: the hosted chat page names one
  // specific thread from an emailed link, and that choice has to stick.
  let conversationId = window.LCW_SESSION?.conversationId;
  let history: any[] = [];
  try {
    if (!conversationId) {
      conversationId = (await api.openConversation()).id;
      saveSession({ ...session, conversationId });
    }
    history = await api.listMessages(conversationId);
  } catch (err: any) {
    if (err?.status === 401 || err?.status === 403) {
      // A sign-in the website gave us can be had again from the website,
      // without troubling the visitor. Once: if the new one is refused too,
      // something else is wrong and the form is the honest answer.
      if (err?.status === 401 && !renewed && session.source === 'site') {
        const again = await renewSiteSession(session);
        if (again) {
          void renderChat(root, config, again, true);
          return;
        }
      }
      // Token expired or revoked — the stored session is worthless, so send
      // them back through OTP rather than showing an empty chat.
      clearSession();
      renderOnboarding(root, config);
      return;
    }
    if (err?.status === 404) {
      // The cached conversation is gone; ask the server for the current one.
      // openConversation resumes an existing thread when there is one.
      try {
        conversationId = (await api.openConversation()).id;
        saveSession({ ...session, conversationId });
        history = await api.listMessages(conversationId);
      } catch {
        clearSession();
        renderOnboarding(root, config);
        return;
      }
    } else {
      // Network or server error. The session is probably still good, so keep
      // it and let them retry instead of forcing OTP again.
      renderChatError(root, config, session);
      return;
    }
  }
  const cid = conversationId!;
  // Closed (the X, or the launcher) while the thread was still loading. There
  // is nothing to draw into and nothing has been read; the launcher, which
  // took over when the panel went, is told there may be a conversation now.
  if (!root.isConnected) {
    window.dispatchEvent(new Event(SESSION_EVENT));
    return;
  }

  function render(msg: any) {
    // Once only. After a reconnect the thread is fetched again to pick up what
    // was missed, and a message can then arrive by both routes.
    if (bodyEl.querySelector(`.lcw-msg[data-msg-id="${String(msg.id)}"]`)) return;
    const el = document.createElement('div');
    el.className = `lcw-msg ${msg.sender_type}`;
    el.dataset.msgId = String(msg.id);
    if (msg.type === 'deleted') {
      el.classList.add('deleted');
      el.textContent = 'This message was deleted';
      bodyEl.appendChild(el);
      bodyEl.scrollTop = bodyEl.scrollHeight;
      return;
    }
    if (msg.reply_to) {
      const q = document.createElement('div');
      q.className = 'lcw-quote';
      const who = document.createElement('strong');
      who.textContent = msg.reply_to.sender_type === 'visitor' ? 'You' : config.brandName;
      const what = document.createElement('span');
      what.textContent = messageSummary(msg.reply_to);
      q.append(who, what);
      el.appendChild(q);
    }
    if (msg.type === 'text') {
      const text = String(msg.body ?? '');
      const cards = (Array.isArray(msg.cards) ? msg.cards : [])
        .map(buildLinkCard)
        .filter((c: HTMLElement | null): c is HTMLElement => c !== null);
      // A message that is nothing but the link shows as the card alone: the
      // address under it would only say the same thing twice, less clearly.
      const linkOnly = cards.length === 1 && /^https?:\/\/[^\s<>"'`]+$/i.test(text.trim());
      if (!linkOnly) {
        const t = document.createElement('span');
        appendLinked(t, text);
        el.appendChild(t);
      }
      if (cards.length) el.classList.add('has-card');
      for (const c of cards) el.appendChild(c);
    } else if (msg.type === 'image' && msg.attachments?.[0]?.url) {
      const img = document.createElement('img');
      img.src = msg.attachments[0].url;
      img.alt = msg.attachments[0].original_name ?? '';
      img.addEventListener('click', (e) => {
        // Otherwise the bubble's own click handler closes the action menu and
        // swallows this.
        e.stopPropagation();
        openLightbox(msg.attachments[0].url, img.alt);
      });
      el.appendChild(img);
    } else if (msg.type === 'video' && msg.attachments?.[0]?.url) {
      const video = document.createElement('video');
      video.controls = true;
      video.preload = 'metadata';
      video.src = msg.attachments[0].url;
      el.appendChild(video);
    } else if (msg.type === 'voice' && msg.attachments?.[0]?.url) {
      el.appendChild(
        buildVoiceNote(msg.attachments[0].url, String(msg.id), msg.attachments[0].duration_ms),
      );
    } else if (msg.attachments?.[0]?.url) {
      el.appendChild(
        buildDocumentCard(
          msg.attachments[0].url,
          msg.attachments[0].original_name,
          msg.attachments[0].size_bytes,
        ),
      );
    }
    // Ticks on our own messages only — a receipt on the agent's bubble tells
    // the visitor nothing. Tagged with the id so the read broadcast can update
    // them in place instead of re-rendering the thread.
    if (msg.sender_type === 'visitor') {
      const meta = document.createElement('span');
      meta.className = msg.read_at ? 'lcw-ticks read' : 'lcw-ticks';
      meta.dataset.msgId = String(msg.id);
      meta.textContent = msg.read_at ? '✓✓' : '✓';
      el.appendChild(meta);
    }

    // Action menu. "Delete for everyone" is only offered on the visitor's own
    // messages — the server rejects it on the agent's, so showing it there
    // would just be a button that always fails.
    const menuBtn = document.createElement('button');
    menuBtn.className = 'lcw-msg-menu-btn';
    menuBtn.type = 'button';
    menuBtn.setAttribute('aria-label', 'Message actions');
    menuBtn.textContent = '⋮';
    menuBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      openMenu(el, msg, msg.sender_type === 'visitor');
    });
    el.appendChild(menuBtn);

    // With the hover trigger the reaction row appears on the bubble itself, so
    // reacting costs no clicks. Skipped on touch: there is no hover there, and
    // the row would either never appear or never leave.
    if (config.emoji.enabled && config.emoji.trigger === 'hover' && !isTouch()) {
      let row: HTMLDivElement | null = null;
      el.addEventListener('mouseenter', () => {
        if (row) return;
        row = document.createElement('div');
        row.className = 'lcw-hover-emoji';
        // Sits on whichever side the bubble is not aligned to, so it never
        // covers the message it belongs to.
        row.style[msg.sender_type === 'visitor' ? 'right' : 'left'] = '6px';
        for (const e of emojiSet()) {
          const b = document.createElement('button');
          b.type = 'button';
          b.textContent = e;
          b.setAttribute('aria-label', `React ${e}`);
          b.addEventListener('click', (ev) => {
            ev.stopPropagation();
            void api.react(String(msg.id), e);
          });
          row.appendChild(b);
        }
        el.appendChild(row);
      });
      el.addEventListener('mouseleave', () => {
        row?.remove();
        row = null;
      });
    }

    renderReactions(el, msg.reactions ?? []);

    bodyEl.appendChild(el);
    bodyEl.scrollTop = bodyEl.scrollHeight;
  }

  /** Replace a bubble's reaction row in place. */
  function renderReactions(bubble: HTMLElement, reactions: any[]) {
    bubble.querySelector('.lcw-reactions')?.remove();
    if (!reactions.length) return;
    const row = document.createElement('div');
    row.className = 'lcw-reactions';
    for (const r of reactions) {
      const chip = document.createElement('button');
      chip.type = 'button';
      // `mine` is decided here from the actor list rather than trusted from
      // the server, since the broadcast is shared by every viewer.
      if (r.actors.includes(myKey)) chip.classList.add('mine');
      chip.textContent = `${r.emoji} ${r.count}`;
      chip.addEventListener('click', (e) => {
        e.stopPropagation();
        void api.react(String(bubble.dataset.msgId), r.emoji);
      });
      row.appendChild(chip);
    }
    bubble.appendChild(row);
  }

  function closeMenu() {
    root.querySelector('.lcw-msg-menu')?.remove();
  }

  function openMenu(bubble: HTMLElement, msg: any, own: boolean) {
    const messageId = String(msg.id);
    const existing = root.querySelector('.lcw-msg-menu') as HTMLElement | null;
    if (existing?.dataset.for === messageId) {
      closeMenu();
      return;
    }
    closeMenu();
    const menu = document.createElement('div');
    menu.className = 'lcw-msg-menu';
    menu.dataset.for = messageId;

    if (config.emoji.enabled) {
      const emojiRow = document.createElement('div');
      emojiRow.className = 'lcw-emoji-row';
      for (const e of emojiSet()) {
        const b = document.createElement('button');
        b.type = 'button';
        b.textContent = e;
        b.setAttribute('aria-label', `React ${e}`);
        b.addEventListener('click', (ev) => {
          ev.stopPropagation();
          closeMenu();
          void api.react(messageId, e);
        });
        emojiRow.appendChild(b);
      }
      menu.appendChild(emojiRow);
    }

    const replyBtn = document.createElement('button');
    replyBtn.type = 'button';
    replyBtn.textContent = 'Reply';
    replyBtn.addEventListener('click', (ev) => {
      ev.stopPropagation();
      closeMenu();
      setReplyTo(msg);
    });
    menu.appendChild(replyBtn);

    const mine = document.createElement('button');
    mine.type = 'button';
    mine.textContent = 'Delete for me';
    mine.addEventListener('click', async (e) => {
      e.stopPropagation();
      closeMenu();
      await api.deleteMessage(messageId, 'me');
      // Nobody else is affected, so there's no broadcast — remove it locally.
      bubble.remove();
    });
    menu.appendChild(mine);

    if (own) {
      const all = document.createElement('button');
      all.type = 'button';
      all.className = 'danger';
      all.textContent = 'Delete for everyone';
      all.addEventListener('click', async (e) => {
        e.stopPropagation();
        closeMenu();
        // The socket broadcast turns it into a tombstone on every client,
        // including this one, so nothing to update here.
        await api.deleteMessage(messageId, 'everyone');
      });
      menu.appendChild(all);
    }

    bubble.appendChild(menu);
  }

  // Any click outside the menu dismisses it.
  root.addEventListener('click', closeMenu);

  // --- Reply composer -----------------------------------------------------
  const replyBar = root.querySelector('.lcw-reply-bar') as HTMLDivElement;
  const replyWho = replyBar.querySelector('strong') as HTMLElement;
  const replyWhat = replyBar.querySelector('span') as HTMLElement;
  let replyTarget: any | null = null;

  function setReplyTo(msg: any | null) {
    replyTarget = msg;
    replyBar.classList.toggle('on', Boolean(msg));
    if (!msg) return;
    replyWho.textContent = msg.sender_type === 'visitor' ? 'You' : config.brandName;
    replyWhat.textContent = messageSummary(msg);
    inputEl.focus();
  }
  (replyBar.querySelector('.lcw-reply-cancel') as HTMLButtonElement).addEventListener('click', (e) => {
    e.stopPropagation();
    setReplyTo(null);
  });

  history.forEach(render);
  // The panel is open in front of the visitor, so the thread is read.
  void api.markRead(cid);

  const socket = connect(config, session.visitorToken);
  // On every connect, not once: a reconnected socket has left every room it
  // was in. See the same note on the launcher's watcher above.
  //
  // And after the first one, fetch the thread again. Whatever was sent while
  // the connection was down -- a deploy restarting the server, a phone coming
  // back from sleep, a train tunnel -- was broadcast to a room this socket was
  // not in, and would otherwise stay invisible until the page was reloaded.
  let connectedBefore = false;
  async function catchUp(): Promise<void> {
    let latest: any[];
    try {
      latest = await api.listMessages(cid);
    } catch {
      return; // still unreachable; the next reconnect tries again
    }
    // The panel may have been closed, or signed out of, while that was in
    // flight. Nothing to draw into, and nothing here has been read.
    if (!root.isConnected || !root.querySelector('.lcw-composer')) return;
    const bubble = (id: unknown) =>
      bodyEl.querySelector(`.lcw-msg[data-msg-id="${String(id)}"]`) as HTMLElement | null;
    let newFromAgent = false;
    // Newest first, so each missed message can be put in front of the one that
    // follows it. A live message can land between reconnecting and this fetch
    // returning; appending the missed ones would then leave them below it.
    let next: HTMLElement | null = null;
    for (const msg of [...latest].reverse()) {
      let shown = bubble(msg.id);
      if (!shown) {
        render(msg);
        shown = bubble(msg.id);
        if (shown && next) bodyEl.insertBefore(shown, next);
        if (msg.sender_type === 'admin' && msg.type !== 'deleted') newFromAgent = true;
      } else if (msg.type === 'deleted') {
        if (!shown.classList.contains('deleted')) {
          shown.classList.add('deleted');
          shown.textContent = 'This message was deleted';
        }
      } else {
        if (msg.read_at) {
          const ticks = shown.querySelector('.lcw-ticks:not(.read)');
          if (ticks) {
            ticks.classList.add('read');
            ticks.textContent = '\u2713\u2713';
          }
        }
        renderReactions(shown, msg.reactions ?? []);
      }
      if (shown) next = shown;
    }
    if (newFromAgent) {
      void api.markRead(cid);
      playNotifySound();
    }
  }
  socket.on('connect', () => {
    if (session.source === 'site') sayHere(socket);
    socket.emit('conversation:join', cid);
    if (connectedBefore) void catchUp();
    connectedBefore = true;
  });

  /*
   * A sign-in can be ended from the server's side while the chat is open (the
   * site's owner changing its secret keys ends every one at once). The server
   * then closes this connection, and the library does not come back by itself
   * after being told to go -- so the chat would sit there looking normal,
   * receiving nothing, and the next message typed into it would be refused.
   *
   * So: try once more, and if the sign-in is refused, say so by returning to
   * the form that issues a new one. The conversation is kept; signing in with
   * the same email resumes it.
   */
  const chatShown = () => root.isConnected && !!root.querySelector('.lcw-composer');
  function signInAgain() {
    if (!chatShown()) return;
    clearSession();
    renderOnboarding(root, config);
    const hint = root.querySelector('.lcw-hint');
    if (hint) {
      hint.textContent =
        'Please confirm your email again to carry on. Your conversation is saved and will be here when you are back in.';
    }
  }
  socket.on('disconnect', (reason) => {
    if (reason === 'io server disconnect' && chatShown()) socket.connect();
  });
  socket.on('connect_error', (error: any) => {
    if (error?.message === 'unauthorized') {
      socket.disconnect();
      signInAgain();
    }
  });

  /*
   * Closing the panel, or signing out of it, used to just change what's on
   * screen and leave this socket connected forever in the background --
   * nothing here ever called `.disconnect()` on it. A leaked socket is still
   * a socket as far as presence tracking is concerned, so the conversation
   * kept reading "online" long past when the visitor believed they had left --
   * and worse, its `message` handler below kept marking every reply that
   * arrived afterward as read, on a screen the visitor was no longer looking
   * at, because nothing told it that screen was gone.
   *
   * Two different things can make that happen, so this checks for both:
   * `root` itself leaving the document (the launcher's own toggle, a close
   * button) and `root` staying but losing the chat's own markup from under it
   * (sign out swaps in the onboarding form via `root.innerHTML =`, without
   * ever removing `root`). `.lcw-composer` only exists while the chat is the
   * thing actually rendered inside `root`.
   */
  const closeWatcher = new MutationObserver(() => {
    if (root.isConnected && root.querySelector('.lcw-composer')) return;
    socket.emit('conversation:leave', cid);
    socket.disconnect();
    closeWatcher.disconnect();
  });
  closeWatcher.observe(document.body, { childList: true });
  closeWatcher.observe(root, { childList: true });

  socket.on('message', (msg: any) => {
    if (msg.conversation_id === cid) {
      // Already on screen means the catch-up after a reconnect got to it
      // first, and has chimed and marked it read already.
      const already = !!bodyEl.querySelector(`.lcw-msg[data-msg-id="${String(msg.id)}"]`);
      render(msg);
      // Panel is open, so an arriving agent message is read on arrival.
      if (msg.sender_type === 'admin' && !already) {
        void api.markRead(cid);
        playNotifySound();
      }
    }
    // Whatever they were typing has now arrived, so drop the indicator
    // rather than waiting for it to time out.
    showTyping(false);
  });

  socket.on('message:reaction', (p: { conversationId: string; messageId: string; reactions: any[] }) => {
    if (p.conversationId !== cid) return;
    const bubble = bodyEl.querySelector(`[data-msg-id="${p.messageId}"]`) as HTMLElement | null;
    if (bubble) renderReactions(bubble, p.reactions);
  });

  socket.on('message:deleted', (p: { conversationId: string; messageId: string }) => {
    if (p.conversationId !== cid) return;
    // Convert in place to a tombstone rather than removing the bubble, so the
    // thread doesn't jump under the visitor mid-read.
    const bubble = bodyEl.querySelector(`[data-msg-id="${p.messageId}"]`) as HTMLElement | null;
    if (!bubble) return;
    bubble.classList.add('deleted');
    bubble.classList.remove('has-card');
    bubble.textContent = 'This message was deleted';
  });

  socket.on('messages:read', (p: { conversationId: string; reader: string }) => {
    // The agent read our messages — flip our ticks in place.
    if (p.conversationId !== cid || p.reader !== 'admin') return;
    bodyEl.querySelectorAll('.lcw-ticks:not(.read)').forEach((el) => {
      el.classList.add('read');
      el.textContent = '✓✓';
    });
  });

  // --- Typing indicator ---------------------------------------------------
  const typingEl = root.querySelector('.lcw-typing') as HTMLDivElement;
  let hideTimer: number | undefined;

  function showTyping(on: boolean) {
    typingEl.classList.toggle('on', on);
    if (on) bodyEl.scrollTop = bodyEl.scrollHeight;
  }

  socket.on('typing', (p: { actor: string; isTyping: boolean }) => {
    // The server only relays to the *other* sockets in the room, so anything
    // arriving here is the agent, not our own echo.
    showTyping(!!p.isTyping);
    window.clearTimeout(hideTimer);
    // Safety net: if the agent closes the tab mid-type we never get the
    // isTyping:false, so expire it ourselves.
    if (p.isTyping) hideTimer = window.setTimeout(() => showTyping(false), 4000);
  });

  // Outbound: throttled heartbeat rather than a single leading-edge event.
  // The receiver expires the indicator after 4s to survive a dropped client,
  // so a long uninterrupted burst of typing has to keep refreshing it — one
  // event at the start would let the indicator vanish mid-sentence.
  let typingSent = false;
  let lastTypingAt = 0;
  let stopTimer: number | undefined;
  inputEl.addEventListener('input', () => {
    const now = Date.now();
    if (now - lastTypingAt > 1500) {
      lastTypingAt = now;
      typingSent = true;
      socket.emit('typing', { conversationId: cid, isTyping: true });
    }
    window.clearTimeout(stopTimer);
    stopTimer = window.setTimeout(() => {
      typingSent = false;
      lastTypingAt = 0;
      socket.emit('typing', { conversationId: cid, isTyping: false });
    }, 2000);
  });

  async function send() {
    const text = inputEl.value.trim();
    if (!text) return;
    inputEl.value = '';
    // The message itself supersedes the indicator; clear it immediately so the
    // agent doesn't see "typing" linger after the bubble lands.
    window.clearTimeout(stopTimer);
    // Reset the throttle so typing again right after sending re-announces
    // immediately rather than waiting out the previous window.
    lastTypingAt = 0;
    if (typingSent) {
      typingSent = false;
      socket.emit('typing', { conversationId: cid, isTyping: false });
    }
    const quoted = replyTarget?.id;
    setReplyTo(null);
    try {
      await api.sendText(cid, text, quoted);
    } catch (err: any) {
      if (err?.status === 401) {
        // The sign-in is no longer accepted. Keep what they wrote for when
        // they are back in, and take them there.
        unsentDraft = text;
        signInAgain();
        return;
      }
      if (isBlockedError(err)) {
        showBlocked();
        return;
      }
      // Not sent, for whatever reason: put it back rather than lose it.
      if (root.isConnected && !inputEl.value) inputEl.value = text;
    }
  }
  (root.querySelector('.lcw-send') as HTMLButtonElement).addEventListener('click', send);
  inputEl.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') send();
  });

  (root.querySelector('.lcw-attach') as HTMLButtonElement).addEventListener('click', () => fileEl.click());
  fileEl.addEventListener('change', async () => {
    const f = fileEl.files?.[0];
    if (!f) return;
    if (f.size > maxFileMb * 1024 * 1024) {
      showNotice(tooLargeText);
      fileEl.value = '';
      return;
    }
    try {
      await api.uploadFile(cid, f, { filename: f.name });
    } catch (err: any) {
      if (!explainUploadError(err)) showNotice('The file could not be sent. Please try again.');
    }
    fileEl.value = '';
  });

  // Voice recording -- MediaRecorder captures WebM/Opus.
  let recorder: MediaRecorder | null = null;
  let chunks: Blob[] = [];
  let startedAt = 0;
  micBtn.addEventListener('click', async () => {
    if (recorder && recorder.state === 'recording') {
      recorder.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunks = [];
      startedAt = Date.now();
      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        micBtn.classList.remove('recording');
        micBtn.innerHTML = ICON.mic;
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const duration = Date.now() - startedAt;
        try {
          await api.uploadFile(cid, blob, { filename: `voice-${Date.now()}.webm`, durationMs: duration });
        } catch (err: any) {
          explainUploadError(err);
        }
      };
      recorder.start();
      micBtn.classList.add('recording');
      micBtn.innerHTML = ICON.stop;
    } catch {
      alert('Microphone permission is required to record voice messages.');
    }
  });
}
